<script setup lang="ts">
/**
 * Split-popover date range picker. One trigger pill that opens a popover
 * containing presets (left) + an inline range calendar (right). Replaces the
 * SelectButton row + conditional DatePicker that used to live in the filter
 * row of orders / lab orders.
 *
 * Wire the v-model:preset / v-model:date-range to the refs returned by
 * `useDateRangePresets`. Selecting a non-custom preset commits immediately and
 * closes; switching to "Custom range" stages a pending range that the user
 * commits with Apply (or discards via Cancel / outside click).
 */
import { computed, ref, watch } from 'vue';
import Popover from 'primevue/popover';
import DatePicker from 'primevue/datepicker';
import Button from 'primevue/button';
import moment from 'moment';
import type {
  DateRangePreset,
  DateRangePresetKey,
} from '@/composables/useDateRangePresets';

interface Props {
  preset: DateRangePresetKey;
  dateRange: [Date | null, Date | null];
  options: DateRangePreset[];
  minDate: Date;
  maxDate: Date;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  'update:preset': [value: DateRangePresetKey];
  'update:dateRange': [value: [Date | null, Date | null]];
}>();

const popoverRef = ref<InstanceType<typeof Popover> | null>(null);

/** Staged values shown in the popover. Committed on preset click or Apply. */
const pendingPreset = ref<DateRangePresetKey>(props.preset);
const pendingRange = ref<[Date | null, Date | null]>([
  props.dateRange[0],
  props.dateRange[1],
]);

function syncFromProps(): void {
  pendingPreset.value = props.preset;
  pendingRange.value = [props.dateRange[0], props.dateRange[1]];
}

function toggle(event: Event): void {
  syncFromProps();
  popoverRef.value?.toggle(event);
}

function close(): void {
  popoverRef.value?.hide();
}

function selectPreset(key: DateRangePresetKey): void {
  if (key === 'custom') {
    // Stage a custom selection; user still needs to Apply.
    pendingPreset.value = 'custom';
    return;
  }
  emit('update:preset', key);
  close();
}

// PrimeVue's DatePicker shares one v-model across single / range / multi
// modes, so the emitted value is a wide union. We're in range mode, so we
// normalize whatever shape we receive back to a [start, end] tuple.
function onCalendarChange(value: Date | (Date | null)[] | null | undefined): void {
  if (Array.isArray(value)) {
    pendingRange.value = [value[0] ?? null, value[1] ?? null];
  } else if (value instanceof Date) {
    pendingRange.value = [value, null];
  } else {
    pendingRange.value = [null, null];
  }
  if (pendingPreset.value !== 'custom') pendingPreset.value = 'custom';
}

function apply(): void {
  emit('update:preset', 'custom');
  emit('update:dateRange', pendingRange.value);
  close();
}

const triggerLabel = computed(() => {
  if (props.preset !== 'custom') {
    const found = props.options.find((o) => o.key === props.preset);
    return found?.label ?? 'Date range';
  }
  const [s, u] = props.dateRange;
  if (s && u) return `${moment(s).format('MMM D')} – ${moment(u).format('MMM D, YYYY')}`;
  return 'Custom range';
});

const canApply = computed(() => {
  const [s, u] = pendingRange.value;
  return pendingPreset.value === 'custom' && s !== null && u !== null;
});

// Keep pending state honest if the parent mutates props while we're closed
// (e.g. route changes that reset the range).
watch(
  () => props.preset,
  () => syncFromProps(),
);
watch(
  () => props.dateRange,
  () => syncFromProps(),
  { deep: true },
);
</script>

<template>
  <div class="date-range-dropdown">
    <button
      ref="triggerRef"
      type="button"
      class="date-range-dropdown__trigger"
      aria-haspopup="true"
      data-test="date-range-trigger"
      @click="toggle"
    >
      <i class="pi pi-calendar date-range-dropdown__icon" aria-hidden="true" />
      <span class="date-range-dropdown__label">{{ triggerLabel }}</span>
      <i class="pi pi-chevron-down date-range-dropdown__chevron" aria-hidden="true" />
    </button>

    <Popover
      ref="popoverRef"
      :pt="{ root: { class: 'date-range-dropdown__panel' } }"
    >
      <div class="drdd-inner">
        <ul class="drdd-presets" role="radiogroup" aria-label="Date range preset">
          <li v-for="opt in options" :key="opt.key">
            <button
              type="button"
              role="radio"
              :aria-checked="pendingPreset === opt.key"
              :class="['drdd-preset', { 'is-active': pendingPreset === opt.key }]"
              :data-test="`date-range-preset-${opt.key}`"
              @click="selectPreset(opt.key)"
            >
              <span class="drdd-radio" aria-hidden="true" />
              <span>{{ opt.label }}</span>
            </button>
          </li>
        </ul>

        <div class="drdd-picker">
          <DatePicker
            :model-value="pendingRange"
            inline
            selection-mode="range"
            :min-date="minDate"
            :max-date="maxDate"
            :manual-input="false"
            data-test="date-range-calendar"
            @update:model-value="onCalendarChange"
          />
          <div class="drdd-footer">
            <Button
              label="Cancel"
              size="small"
              severity="secondary"
              text
              data-test="date-range-cancel"
              @click="close"
            />
            <Button
              label="Apply"
              size="small"
              :disabled="!canApply"
              data-test="date-range-apply"
              @click="apply"
            />
          </div>
        </div>
      </div>
    </Popover>
  </div>
</template>

<style scoped>
/* Trigger lives inside the component, so scoped styles apply. */
.date-range-dropdown {
  position: relative;
  display: inline-flex;
}
.date-range-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  color: #1f2937;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease, box-shadow 120ms ease;
}
.date-range-dropdown__trigger:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.date-range-dropdown__trigger:focus-visible {
  outline: 0;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.date-range-dropdown__icon {
  font-size: 0.875rem;
  color: #6b7280;
}
.date-range-dropdown__label {
  line-height: 1.25;
}
.date-range-dropdown__chevron {
  font-size: 0.625rem;
  color: #6b7280;
  margin-left: 0.125rem;
}
</style>

<style>
/* PrimeVue's Popover portals to <body>, so panel-content styles must be
 * unscoped. Class names are prefixed `drdd-` to avoid collisions. */
.date-range-dropdown__panel {
  /* Tighten default Popover padding so the two-column layout doesn't get a
   * wide gutter around it. */
}
.date-range-dropdown__panel .p-popover-content {
  padding: 0 !important;
}
.drdd-inner {
  display: grid;
  grid-template-columns: 12rem auto;
  min-width: 30rem;
}
.drdd-presets {
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;
  border-right: 1px solid #e5e7eb;
  background: #f9fafb;
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
}
.drdd-preset {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  font-family: inherit;
}
.drdd-preset:hover {
  background: #f3f4f6;
}
.drdd-preset.is-active {
  color: #1d4ed8;
  font-weight: 600;
  background: #eff6ff;
}
.drdd-radio {
  display: inline-block;
  width: 0.875rem;
  height: 0.875rem;
  border: 1.5px solid #9ca3af;
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
  background: #fff;
}
.drdd-preset.is-active .drdd-radio {
  border-color: #2563eb;
}
.drdd-preset.is-active .drdd-radio::after {
  content: '';
  position: absolute;
  inset: 2px;
  background: #2563eb;
  border-radius: 50%;
}
.drdd-picker {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.drdd-picker .p-datepicker-panel,
.drdd-picker .p-datepicker {
  border: 0;
  box-shadow: none;
  padding: 0;
}
.drdd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f3f4f6;
}
</style>
